import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Home, Building2, Car, PawPrint, TreePine, ShoppingBag,
  Sparkles, Droplets, Layers, MapPin, ArrowRight,
  Truck as TruckIcon, CarFront, Bus, HelpCircle,
  BrushCleaning, ShieldCheck, Info,
  Dog, Cat, Footprints, Clock, Bell, Bone, Moon, Minus, Plus,
  Leaf, DoorOpen, Waves, Trash2,
  Shirt, ClipboardList, MapPinned,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { formatPrice } from "@/lib/api";

// ---------------------------------------------------------------------------
// Service catalog — display-only structure. No pricing, no booking wiring.
// Pricing / booking will be connected in a later step.
// ---------------------------------------------------------------------------
const CATEGORIES = [
  {
    id: "home",
    icon: Home,
    title: "Home Cleaning",
    tag: "Residential",
    tint: "teal",
    highlight: true,
    services: [
      { name: "Standard Cleaning" },
      { name: "Deep Cleaning" },
      { name: "Move-In Cleaning" },
      { name: "Move-Out Cleaning" },
      { name: "Airbnb / Vacation Rental Cleaning" },
    ],
  },
  {
    id: "commercial",
    icon: Building2,
    title: "Commercial Cleaning",
    tag: "Business",
    tint: "sky",
    highlight: true,
    services: [
      { name: "Office Cleaning" },
      { name: "Commercial Cleaning" },
      { name: "Post-Construction Cleaning" },
    ],
  },
  {
    id: "car",
    icon: Car,
    title: "Car Cleaning",
    tag: "Mobile · At-your-location",
    tint: "amber",
    highlight: true,
    services: [
      { name: "Interior Car Cleaning" },
      { name: "Exterior Car Cleaning" },
      { name: "Interior + Exterior" },
      { name: "Deep Interior Cleaning" },
      { name: "Mobile Car Detailing" },
    ],
  },
  {
    id: "pet",
    icon: PawPrint,
    title: "Pet Care",
    tag: "In-home & drop-in",
    tint: "rose",
    highlight: true,
    services: [
      { name: "Dog Walking" },
      { name: "Dog Sitting" },
      { name: "Drop-In Pet Visits" },
      { name: "Pet Feeding" },
      { name: "Pet Waste Cleanup" },
      { name: "Overnight Pet Care" },
    ],
  },
  {
    id: "outdoor",
    icon: TreePine,
    title: "Outdoor Cleaning",
    tag: "Exterior",
    tint: "emerald",
    highlight: true,
    services: [
      { name: "Yard Cleanup" },
      { name: "Patio / Outdoor Cleaning" },
      { name: "Garage Cleaning" },
      { name: "Pressure Washing" },
    ],
  },
  {
    id: "convenience",
    icon: ShoppingBag,
    title: "Convenience Services",
    tag: "Everyday help",
    tint: "violet",
    highlight: true,
    services: [
      { name: "Laundry" },
      { name: "Organization & Decluttering" },
      { name: "Errand Services" },
    ],
  },
];

// ---------------------------------------------------------------------------
// Car Cleaning options — display-only. Selection state is local for now so
// the customer can see how the flow will feel; nothing is submitted anywhere.
// ---------------------------------------------------------------------------
const VEHICLE_TYPES = [
  { id: "sedan", label: "Sedan", icon: CarFront },
  { id: "suv",   label: "SUV",   icon: Car },
  { id: "truck", label: "Truck", icon: TruckIcon },
  { id: "van",   label: "Van",   icon: Bus },
  { id: "other", label: "Other", icon: HelpCircle },
];

const CAR_SERVICES = [
  {
    id: "interior",
    icon: BrushCleaning,
    name: "Interior Cleaning",
    desc: "Vacuum, dust, wipe-down of dashboards, consoles, door panels and mats.",
  },
  {
    id: "exterior",
    icon: Droplets,
    name: "Exterior Cleaning",
    desc: "Full hand wash, wheels, tires, windows and a streak-free finish.",
  },
  {
    id: "both",
    icon: Layers,
    name: "Interior + Exterior",
    desc: "Complete refresh — the interior detail combined with a full exterior wash.",
  },
  {
    id: "deep_interior",
    icon: Sparkles,
    name: "Deep Interior Cleaning",
    desc: "Shampooed carpets, upholstery extraction, stain treatment and odor neutralizing.",
  },
  {
    id: "mobile_detailing",
    icon: TruckIcon,
    name: "Mobile Car Detailing",
    desc: "Full showroom-grade detail — polish, sealants and interior deep clean, at your location.",
    flagship: true,
  },
];

// ---------------------------------------------------------------------------
// Car Cleaning pricing table (USD, base price only — add-ons applied on top).
// Keyed by service id → vehicle id. `null` = custom quote (Mobile Detailing).
// ---------------------------------------------------------------------------
const CAR_PRICING = {
  interior:      { sedan: 75,  suv: 90,  truck: 90,  van: 110 },
  exterior:      { sedan: 50,  suv: 60,  truck: 65,  van: 75  },
  both:          { sedan: 120, suv: 145, truck: 150, van: 175 },
  deep_interior: { sedan: 125, suv: 150, truck: 155, van: 180 },
  mobile_detailing: null,
};

const CAR_ADDONS = [
  { id: "pet_hair",              label: "Pet-hair removal",          price: 25 },
  { id: "carpet",                label: "Carpet / seat shampoo",     price: 30 },
  { id: "odor",                  label: "Odor treatment",            price: 25 },
  { id: "trunk",                 label: "Trunk deep clean",          price: 20 },
  { id: "mess",                  label: "Excessive dirt / mess",     price: 30 },
  { id: "wax_sealant",           label: "Wax / paint sealant",       price: 45 },
  { id: "vacuum",                label: "Full vacuum",               price: 15 },
  { id: "leather_conditioning",  label: "Leather conditioning",      price: 30 },
  { id: "tire_wheel",            label: "Tire & wheel cleaning",     price: 20 },
  { id: "engine_bay",            label: "Engine-bay cleaning",       price: 40 },
  { id: "headlight_restoration", label: "Headlight restoration",     price: 35 },
  { id: "mobile_travel",         label: "Mobile / travel fee",       price: 20 },
];

// Platform fee split (matches the existing marketplace 80/20 model — display only).
const PLATFORM_FEE_PCT = 0.20;

const TINT_STYLES = {
  teal:    { bar: "from-teal-400/60",    ring: "ring-teal-400/40",    icon: "text-teal-300",    chip: "bg-teal-500/10 text-teal-200 border-teal-400/30" },
  sky:     { bar: "from-sky-400/60",     ring: "ring-sky-400/40",     icon: "text-sky-300",     chip: "bg-sky-500/10 text-sky-200 border-sky-400/30" },
  amber:   { bar: "from-amber-400/70",   ring: "ring-amber-400/50",   icon: "text-amber-300",   chip: "bg-amber-500/10 text-amber-200 border-amber-400/30" },
  rose:    { bar: "from-rose-400/60",    ring: "ring-rose-400/40",    icon: "text-rose-300",    chip: "bg-rose-500/10 text-rose-200 border-rose-400/30" },
  emerald: { bar: "from-emerald-400/60", ring: "ring-emerald-400/40", icon: "text-emerald-300", chip: "bg-emerald-500/10 text-emerald-200 border-emerald-400/30" },
  violet:  { bar: "from-violet-400/60",  ring: "ring-violet-400/40",  icon: "text-violet-300",  chip: "bg-violet-500/10 text-violet-200 border-violet-400/30" },
};

function CategoryCard({ cat, onCarClick, onPetClick, onOutdoorClick, onConvenienceClick, onHomeClick, onCommercialClick }) {
  const Icon = cat.icon;
  const s = TINT_STYLES[cat.tint] || TINT_STYLES.teal;
  const isCar = cat.id === "car";
  const isPet = cat.id === "pet";
  const isOutdoor = cat.id === "outdoor";
  const isConv = cat.id === "convenience";
  const isHome = cat.id === "home";
  const isCommercial = cat.id === "commercial";

  return (
    <Card
      data-testid={`service-cat-${cat.id}`}
      className={`relative overflow-hidden bg-white/[0.03] border-white/10 hover:border-white/25 transition-all ${cat.highlight ? "ring-1 " + s.ring : ""}`}
    >
      <div className={`absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r ${s.bar} to-transparent`} />
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center ${s.icon} flex-shrink-0`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 data-testid={`service-cat-title-${cat.id}`} className="font-display font-semibold text-slate-100 text-xl leading-tight">
                {cat.title}
              </h3>
              <span className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${s.chip}`}>
                {cat.tag}
              </span>
            </div>
          </div>
        </div>

        <ul className="mt-5 space-y-2">
          {cat.services.map((sv) => (
            <li
              key={sv.name}
              data-testid={`service-item-${cat.id}-${sv.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="flex items-center gap-2.5 text-sm text-slate-200"
            >
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.icon.replace("text-", "bg-")}`} />
              {sv.name}
            </li>
          ))}
        </ul>

        {isCar && (
          <div className="mt-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-amber-200/90">
              <MapPin className="w-3.5 h-3.5" /> Mobile · we come to you
            </div>
            <Button
              onClick={onCarClick}
              data-testid="services-jump-to-car"
              size="sm"
              className="bg-amber-400/90 hover:bg-amber-300 text-slate-950 font-semibold"
            >
              Explore <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        )}

        {isPet && (
          <div className="mt-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-rose-200/90">
              <PawPrint className="w-3.5 h-3.5" /> In-home visits · walks · overnights
            </div>
            <Button
              onClick={onPetClick}
              data-testid="services-jump-to-pet"
              size="sm"
              className="bg-rose-400/90 hover:bg-rose-300 text-slate-950 font-semibold"
            >
              Explore <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        )}

        {isOutdoor && (
          <div className="mt-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-200/90">
              <TreePine className="w-3.5 h-3.5" /> Yards · patios · garages · pressure wash
            </div>
            <Button
              onClick={onOutdoorClick}
              data-testid="services-jump-to-outdoor"
              size="sm"
              className="bg-emerald-400/90 hover:bg-emerald-300 text-slate-950 font-semibold"
            >
              Explore <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        )}

        {isConv && (
          <div className="mt-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-violet-200/90">
              <ShoppingBag className="w-3.5 h-3.5" /> Laundry · Organizing · Errands
            </div>
            <Button
              onClick={onConvenienceClick}
              data-testid="services-jump-to-convenience"
              size="sm"
              className="bg-violet-400/90 hover:bg-violet-300 text-slate-950 font-semibold"
            >
              Explore <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        )}

        {isHome && (
          <div className="mt-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-teal-200/90">
              <Home className="w-3.5 h-3.5" /> Standard · Deep · Move-in · Airbnb
            </div>
            <Button
              onClick={onHomeClick}
              data-testid="services-jump-to-home"
              size="sm"
              className="bg-teal-400/90 hover:bg-teal-300 text-slate-950 font-semibold"
            >
              Explore <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        )}

        {isCommercial && (
          <div className="mt-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-sky-200/90">
              <Building2 className="w-3.5 h-3.5" /> Office · Retail · Gym · Clinic
            </div>
            <Button
              onClick={onCommercialClick}
              data-testid="services-jump-to-commercial"
              size="sm"
              className="bg-sky-400/90 hover:bg-sky-300 text-slate-950 font-semibold"
            >
              Explore <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        )}

        {!isCar && !isPet && !isOutdoor && !isConv && !isHome && !isCommercial && (
          <div className="mt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
            Booking coming soon
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CarCleaningSection() {
  const nav = useNavigate();
  const { currency } = useApp();
  const [vehicle, setVehicle] = useState(null);
  const [service, setService] = useState(null);
  const [addons, setAddons] = useState([]);

  const toggleAddon = (id) => {
    setAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  // ------------------------------------------------------------------------
  // Live price calc. Rules:
  //  - Vehicle "other" or no vehicle    → custom quote
  //  - Service "mobile_detailing"        → custom quote (no fixed table given)
  //  - Any other combo                   → base + selected add-ons
  //  Display uses the existing marketplace 80/20 split (identical to Landing).
  // ------------------------------------------------------------------------
  const priceState = useMemo(() => {
    if (!service || !vehicle) return { kind: "empty" };
    if (service === "mobile_detailing" || vehicle === "other") return { kind: "quote" };
    const table = CAR_PRICING[service];
    const base = table ? table[vehicle] : null;
    if (base == null) return { kind: "quote" };
    const addonRows = CAR_ADDONS.filter((a) => addons.includes(a.id));
    const addonTotal = addonRows.reduce((s, a) => s + a.price, 0);
    const total = base + addonTotal;
    const platformFee = Math.round(total * PLATFORM_FEE_PCT * 100) / 100;
    const cleanerPayout = Math.round((total - platformFee) * 100) / 100;
    return { kind: "priced", base, addonRows, addonTotal, total, platformFee, cleanerPayout };
  }, [service, vehicle, addons]);

  return (
    <section
      id="car-cleaning"
      data-testid="car-cleaning-section"
      className="relative scroll-mt-28 mt-20"
    >
      <div className="absolute inset-x-0 -top-4 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" />

      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-200 text-[10px] font-mono uppercase tracking-[0.22em]">
          <MapPin className="w-3 h-3" /> Mobile Service · At Your Location
        </div>
        <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl leading-[1.05] text-slate-100">
          Car Cleaning
          <span className="block bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
            wherever you park.
          </span>
        </h2>
        <p className="mt-4 text-slate-400 text-base">
          Driveway, office lot, apartment garage — a vetted CLEANBAZ Pro shows up with everything they need.
          You keep working, we make your car look showroom-fresh.
        </p>
      </div>

      {/* Vehicle Type */}
      <div className="mt-12">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 1 — Vehicle Type</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {VEHICLE_TYPES.map((v) => {
            const active = vehicle === v.id;
            const Icon = v.icon;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setVehicle(v.id)}
                data-testid={`car-vehicle-${v.id}`}
                className={`group relative overflow-hidden rounded-2xl border p-5 text-left transition-all ${
                  active
                    ? "border-amber-400/70 bg-amber-500/10 shadow-[0_0_28px_rgba(245,158,11,0.25)]"
                    : "border-white/10 bg-white/[0.03] hover:border-amber-400/40 hover:bg-white/[0.05]"
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${
                  active ? "bg-amber-400/20 border-amber-400/40 text-amber-200" : "bg-white/[0.03] border-white/10 text-slate-300 group-hover:text-amber-200"
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-3 text-sm font-semibold text-slate-100">{v.label}</div>
                {active && (
                  <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-amber-300 shadow-[0_0_10px_rgba(252,211,77,0.9)]" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Services */}
      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 2 — Choose Service</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CAR_SERVICES.map((sv) => {
            const active = service === sv.id;
            const Icon = sv.icon;
            // Live vehicle-aware price preview shown inside each service card.
            const table = CAR_PRICING[sv.id];
            let priceLabel = null;
            if (sv.id === "mobile_detailing") priceLabel = "Custom quote";
            else if (vehicle && vehicle !== "other" && table && table[vehicle] != null) {
              priceLabel = `From ${formatPrice(table[vehicle], currency)}`;
            } else if (vehicle === "other") priceLabel = "Custom quote";
            else if (table) {
              const min = Math.min(...Object.values(table));
              priceLabel = `From ${formatPrice(min, currency)}`;
            }
            return (
              <button
                key={sv.id}
                type="button"
                onClick={() => setService(sv.id)}
                data-testid={`car-service-${sv.id}`}
                className={`relative overflow-hidden text-left rounded-2xl p-5 border transition-all ${
                  active
                    ? "border-amber-400/70 bg-amber-500/[0.08] shadow-[0_0_32px_rgba(245,158,11,0.22)]"
                    : "border-white/10 bg-white/[0.03] hover:border-amber-400/30 hover:bg-white/[0.05]"
                }`}
              >
                {sv.flagship && (
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-amber-400/20 border border-amber-400/40 text-amber-100 text-[10px] font-mono uppercase tracking-[0.18em]">
                      Flagship
                    </Badge>
                  </div>
                )}
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${
                  active ? "bg-amber-400/20 border-amber-400/40 text-amber-200" : "bg-white/[0.03] border-white/10 text-amber-200/90"
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-4 font-display font-semibold text-slate-100 text-lg">{sv.name}</div>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{sv.desc}</p>
                {priceLabel && (
                  <div
                    data-testid={`car-service-price-${sv.id}`}
                    className={`mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] border ${
                      sv.id === "mobile_detailing" || vehicle === "other"
                        ? "bg-slate-800/60 border-white/15 text-slate-200"
                        : "bg-amber-500/10 border-amber-400/30 text-amber-200"
                    }`}
                  >
                    {priceLabel}
                  </div>
                )}
                {active && (
                  <span className="absolute top-3 left-3 w-2 h-2 rounded-full bg-amber-300 shadow-[0_0_10px_rgba(252,211,77,0.9)]" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Add-ons */}
      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 3 — Optional Add-ons</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {CAR_ADDONS.map((a) => {
            const active = addons.includes(a.id);
            return (
              <label
                key={a.id}
                data-testid={`car-addon-${a.id}`}
                className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-all ${
                  active
                    ? "border-amber-400/60 bg-amber-500/[0.08]"
                    : "border-white/10 bg-white/[0.03] hover:border-amber-400/30"
                }`}
              >
                <Checkbox
                  checked={active}
                  onCheckedChange={() => toggleAddon(a.id)}
                  data-testid={`car-addon-toggle-${a.id}`}
                  className="border-amber-400/50 data-[state=checked]:bg-amber-400 data-[state=checked]:text-slate-950"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-100">{a.label}</div>
                </div>
                <div className="font-mono text-sm text-amber-200">+{formatPrice(a.price, currency)}</div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Live estimate */}
      <div
        data-testid="car-price-estimate"
        className="mt-10 rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/[0.10] via-slate-900/60 to-slate-900/60 p-6 sm:p-7"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-amber-200/80">Live Estimate</div>
            <div className="mt-1 font-display font-bold text-slate-100 text-2xl">
              {priceState.kind === "priced" ? "Your Car Cleaning total" :
               priceState.kind === "quote"  ? "Custom quote required" :
                                               "Pick vehicle + service"}
            </div>
            <div className="mt-1.5 text-sm text-slate-400 max-w-xl">
              {priceState.kind === "priced"
                ? "Payment-protected. Funds released to your CLEANBAZ Pro after service completion."
                : priceState.kind === "quote"
                ? "Mobile Car Detailing and \"Other\" vehicle types are quoted individually — booking opens in the next release."
                : "Choose your vehicle type and a service to see the live price."}
            </div>
          </div>
          <div className="text-right">
            <div data-testid="car-total-price" className="font-display font-black text-4xl sm:text-5xl text-amber-200 leading-none">
              {priceState.kind === "priced" ? formatPrice(priceState.total, currency) : "—"}
            </div>
            {priceState.kind === "priced" && (
              <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400">
                Base + add-ons · Booking opens soon
              </div>
            )}
          </div>
        </div>

        {priceState.kind === "priced" && (
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="rounded-xl bg-slate-950/50 border border-white/10 p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-2">Line items</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-200">
                  <span>Base service</span>
                  <span className="font-mono">{formatPrice(priceState.base, currency)}</span>
                </div>
                {priceState.addonRows.map((a) => (
                  <div key={a.id} className="flex justify-between text-slate-300">
                    <span>+ {a.label}</span>
                    <span className="font-mono">{formatPrice(a.price, currency)}</span>
                  </div>
                ))}
                {priceState.addonRows.length === 0 && (
                  <div className="text-xs text-slate-500 italic">No add-ons selected</div>
                )}
              </div>
            </div>
            <div className="rounded-xl bg-slate-950/50 border border-white/10 p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-2">Marketplace split (80/20)</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-200">
                  <span>Cleaner payout (80%)</span>
                  <span className="font-mono text-teal-300">{formatPrice(priceState.cleanerPayout, currency)}</span>
                </div>
                <div className="flex justify-between text-slate-200">
                  <span>Platform fee (20%)</span>
                  <span className="font-mono text-slate-300">{formatPrice(priceState.platformFee, currency)}</span>
                </div>
              </div>
              <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-500">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Same payment-protection flow as every other CLEANBAZ service.</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Why mobile */}
      <div className="mt-12 grid md:grid-cols-3 gap-4">
        {[
          { icon: MapPin,      title: "At your location",   desc: "Home, office, garage, apartment lot — we bring the water, power and supplies." },
          { icon: ShieldCheck, title: "Vetted CLEANBAZ Pro", desc: "ID-verified, insurance-on-file detailers only. Same payment-protected flow as every service." },
          { icon: TruckIcon,   title: "Fully equipped",     desc: "Waterless / low-water systems available — no hookup needed for most locations." },
        ].map((b) => (
          <div key={b.title} data-testid={`car-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="w-10 h-10 rounded-lg bg-amber-400/10 border border-amber-400/30 text-amber-200 flex items-center justify-center">
              <b.icon className="w-5 h-5" />
            </div>
            <div className="mt-3 font-display font-semibold text-slate-100">{b.title}</div>
            <p className="mt-1.5 text-sm text-slate-400">{b.desc}</p>
          </div>
        ))}
      </div>

      {/* Notify CTA — no booking flow yet */}
      <div className="mt-10 rounded-2xl border border-amber-400/30 bg-gradient-to-r from-amber-500/[0.08] via-amber-500/[0.05] to-transparent p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
        <div>
          <div className="font-display font-bold text-slate-100 text-xl">
            {priceState.kind === "priced" ? "Ready to book your Car Cleaning?" : "Booking opens soon."}
          </div>
          <p className="text-sm text-slate-400 mt-1">
            {priceState.kind === "priced"
              ? "Continue to checkout — service address & schedule are set on the next screen."
              : "Car cleaning is live in the catalog. Instant booking & live pricing arrive in the next release."}
          </p>
        </div>
        {priceState.kind === "priced" ? (
          <Button
            onClick={() => {
              const params = new URLSearchParams({
                booking_type: "car",
                vehicle: vehicle || "",
                car_service: service || "",
              });
              if (addons.length) params.set("car_addons", addons.join(","));
              nav(`/book?${params.toString()}`);
            }}
            data-testid="car-book-cta"
            className="bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold px-6 h-11"
          >
            Book this car cleaning <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        ) : (
          <Button
            onClick={() => nav("/signup")}
            data-testid="car-notify-cta"
            className="bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold px-6 h-11"
          >
            Notify me when live <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Pet Care — full booking flow. Mirrors the Car Cleaning UX (service picker,
// pet count, add-ons, live estimate, book) and posts to the same /jobs API
// with booking_type=pet.
// ---------------------------------------------------------------------------
const PET_SERVICES = [
  { id: "dog_walking",   icon: Footprints, name: "Dog Walking",         desc: "Neighborhood walk with your pup — 30 or 60 min sessions.", durations: ["30", "60"] },
  { id: "drop_in",       icon: Bell,       name: "Drop-In Pet Visits",  desc: "Quick check-in — feed, refill water, play, potty break.", durations: ["30", "60"] },
  { id: "feeding",       icon: Bone,       name: "Pet Feeding",         desc: "Short visit focused on meals, fresh water, and a quick check.", durations: null },
  { id: "waste_cleanup", icon: Sparkles,   name: "Pet Waste Cleanup",   desc: "Yard scoop & sanitize. Small-to-medium yards.",             durations: null },
  { id: "dog_sitting",   icon: Dog,        name: "Dog Sitting",         desc: "Full-day sitting at the owner's home — company, meals, walks.", durations: null, flagship: true },
  { id: "overnight",     icon: Moon,       name: "Overnight Pet Care",  desc: "Overnight stay at the owner's home — includes evening & morning care.", durations: null },
];

const PET_BASE_PRICE = {
  dog_walking:   { "30": 25, "60": 40 },
  drop_in:       { "30": 25, "60": 38 },
  feeding:       18,
  waste_cleanup: 30,
  dog_sitting:   65,
  overnight:     95,
};

const PET_ADDONS = [
  { id: "extra_pet",       label: "Extra pet",                 price: 10, per: "each additional" },
  { id: "medication",      label: "Medication administration", price: 8 },
  { id: "weekend",         label: "Weekend / holiday",         price: 15 },
  { id: "key_pickup",      label: "Key pickup / return",       price: 10 },
  { id: "extra_walk_time", label: "Additional walk time",      price: 15 },
  { id: "feeding",         label: "Feeding",                   price: 10 },
  { id: "waste_cleanup",   label: "Waste cleanup",             price: 15 },
  { id: "special_care",    label: "Special-care instructions", price: 12 },
];
const PET_ADDON_META = Object.fromEntries(PET_ADDONS.map((a) => [a.id, a]));

// CLEANBAZ Pet Care is priced in USD on the backend. Force USD in every
// display within the Pet flow, regardless of the user's global currency
// preference, so customers see the same numbers as the backend calc_price.
const PET_CURRENCY = "USD";

function PetCareSection() {
  const nav = useNavigate();
  const { currency } = useApp();
  const [service, setService] = useState(null);
  const [duration, setDuration] = useState("30");
  const [petCount, setPetCount] = useState(1);
  const [addons, setAddons] = useState([]);

  const activeSvc = PET_SERVICES.find((s) => s.id === service) || null;
  const hasDurations = !!activeSvc?.durations;

  const toggleAddon = (id) => {
    setAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };
  const bumpPetCount = (delta) =>
    setPetCount((p) => Math.max(1, Math.min(5, p + delta)));

  // Live pricing — mirrors backend calc_price("pet") exactly.
  const priceState = useMemo(() => {
    if (!service) return { kind: "empty" };
    const entry = PET_BASE_PRICE[service];
    const base = typeof entry === "object"
      ? entry[hasDurations ? duration : "30"]
      : entry;
    if (base == null) return { kind: "empty" };
    // Add-on breakdown. extra_pet scales with (petCount - 1).
    const addonRows = [];
    let addonTotal = 0;
    addons.forEach((id) => {
      const meta = PET_ADDON_META[id];
      if (!meta) return;
      if (id === "extra_pet") {
        const extras = Math.max(0, petCount - 1);
        if (extras > 0) {
          addonRows.push({ id, label: `${meta.label} × ${extras}`, price: meta.price * extras });
          addonTotal += meta.price * extras;
        }
      } else {
        addonRows.push({ id, label: meta.label, price: meta.price });
        addonTotal += meta.price;
      }
    });
    const total = base + addonTotal;
    const platformFee = Math.round(total * PLATFORM_FEE_PCT * 100) / 100;
    const cleanerPayout = Math.round((total - platformFee) * 100) / 100;
    return { kind: "priced", base, addonRows, addonTotal, total, platformFee, cleanerPayout };
  }, [service, duration, petCount, addons, hasDurations]);

  return (
    <section
      id="pet-care"
      data-testid="pet-care-section"
      className="relative scroll-mt-28 mt-24"
    >
      <div className="absolute inset-x-0 -top-4 h-px bg-gradient-to-r from-transparent via-rose-400/50 to-transparent" />

      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-400/30 text-rose-200 text-[10px] font-mono uppercase tracking-[0.22em]">
          <PawPrint className="w-3 h-3" /> In-home Pet Care · At Your Door
        </div>
        <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl leading-[1.05] text-slate-100">
          Pet Care
          <span className="block bg-gradient-to-r from-rose-300 to-rose-500 bg-clip-text text-transparent">
            when you can't be home.
          </span>
        </h2>
        <p className="mt-4 text-slate-400 text-base">
          Vetted CLEANBAZ Pros walk, feed, sit, and stay overnight with your pet — same
          payment-protected flow as every other service.
        </p>
      </div>

      {/* Service */}
      <div className="mt-12">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 1 — Choose Service</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PET_SERVICES.map((sv) => {
            const active = service === sv.id;
            const Icon = sv.icon;
            // vehicle-agnostic price hint — for duration services use the 30-min baseline.
            const entry = PET_BASE_PRICE[sv.id];
            const priceHint = typeof entry === "object"
              ? `From ${formatPrice(Math.min(...Object.values(entry)), PET_CURRENCY)}`
              : `From ${formatPrice(entry, PET_CURRENCY)}`;
            return (
              <button
                key={sv.id}
                type="button"
                onClick={() => setService(sv.id)}
                data-testid={`pet-service-${sv.id}`}
                className={`relative overflow-hidden text-left rounded-2xl p-5 border transition-all ${
                  active
                    ? "border-rose-400/70 bg-rose-500/[0.08] shadow-[0_0_32px_rgba(244,63,94,0.22)]"
                    : "border-white/10 bg-white/[0.03] hover:border-rose-400/30 hover:bg-white/[0.05]"
                }`}
              >
                {sv.flagship && (
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-rose-400/20 border border-rose-400/40 text-rose-100 text-[10px] font-mono uppercase tracking-[0.18em]">
                      Popular
                    </Badge>
                  </div>
                )}
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${
                  active ? "bg-rose-400/20 border-rose-400/40 text-rose-200" : "bg-white/[0.03] border-white/10 text-rose-200/90"
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-4 font-display font-semibold text-slate-100 text-lg">{sv.name}</div>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{sv.desc}</p>
                <div
                  data-testid={`pet-service-price-${sv.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] border bg-rose-500/10 border-rose-400/30 text-rose-200"
                >
                  {priceHint}
                </div>
                {active && (
                  <span className="absolute top-3 left-3 w-2 h-2 rounded-full bg-rose-300 shadow-[0_0_10px_rgba(251,113,133,0.9)]" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Duration (only for services that support it) */}
      {hasDurations && (
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 2 — Session Length</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            {activeSvc.durations.map((d) => {
              const active = duration === d;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDuration(d)}
                  data-testid={`pet-duration-${d}`}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    active
                      ? "border-rose-400/70 bg-rose-500/10"
                      : "border-white/10 bg-white/[0.03] hover:border-rose-400/40"
                  }`}
                >
                  <div className="flex items-center gap-2 text-slate-100 font-semibold">
                    <Clock className="w-4 h-4 text-rose-300" /> {d} minutes
                  </div>
                  <div className="mt-1 text-xs font-mono text-rose-200">
                    {formatPrice(PET_BASE_PRICE[service][d], PET_CURRENCY)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Pet count */}
      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">
            {hasDurations ? "Step 3" : "Step 2"} — How Many Pets?
          </span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 max-w-md">
          <Cat className="w-6 h-6 text-rose-300" />
          <div className="flex-1">
            <div className="text-slate-100 font-semibold">Pets in the household</div>
            <div className="text-xs text-slate-500 mt-0.5">Extra-pet fee applies from the 2nd pet onward.</div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => bumpPetCount(-1)}
              disabled={petCount <= 1}
              data-testid="pet-count-decrement"
              size="sm"
              variant="outline"
              className="w-9 h-9 p-0 rounded-full border-rose-400/40 text-rose-200 hover:bg-rose-500/10"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <div data-testid="pet-count-value" className="w-8 text-center font-display font-bold text-xl text-slate-100">
              {petCount}
            </div>
            <Button
              onClick={() => bumpPetCount(1)}
              disabled={petCount >= 5}
              data-testid="pet-count-increment"
              size="sm"
              variant="outline"
              className="w-9 h-9 p-0 rounded-full border-rose-400/40 text-rose-200 hover:bg-rose-500/10"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Add-ons */}
      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">
            {hasDurations ? "Step 4" : "Step 3"} — Optional Add-ons
          </span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {PET_ADDONS.map((a) => {
            const active = addons.includes(a.id);
            return (
              <label
                key={a.id}
                data-testid={`pet-addon-${a.id}`}
                className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-all ${
                  active
                    ? "border-rose-400/60 bg-rose-500/[0.08]"
                    : "border-white/10 bg-white/[0.03] hover:border-rose-400/30"
                }`}
              >
                <Checkbox
                  checked={active}
                  onCheckedChange={() => toggleAddon(a.id)}
                  data-testid={`pet-addon-toggle-${a.id}`}
                  className="border-rose-400/50 data-[state=checked]:bg-rose-400 data-[state=checked]:text-slate-950"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-100">{a.label}</div>
                  {a.per && <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-500 mt-0.5">{a.per}</div>}
                </div>
                <div className="font-mono text-sm text-rose-200">+{formatPrice(a.price, PET_CURRENCY)}</div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Live estimate */}
      <div
        data-testid="pet-price-estimate"
        className="mt-10 rounded-2xl border border-rose-400/40 bg-gradient-to-br from-rose-500/[0.10] via-slate-900/60 to-slate-900/60 p-6 sm:p-7"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-rose-200/80">Live Estimate · USD</div>
            <div className="mt-1 font-display font-bold text-slate-100 text-2xl">
              {priceState.kind === "priced" ? "Your Pet Care total" : "Pick a service"}
            </div>
            <div className="mt-1.5 text-sm text-slate-400 max-w-xl">
              {priceState.kind === "priced"
                ? "Payment-protected. Funds released to your CLEANBAZ Pro after completion."
                : "Choose a service above to see your live price."}
            </div>
          </div>
          <div className="text-right">
            <div data-testid="pet-total-price" className="font-display font-black text-4xl sm:text-5xl text-rose-200 leading-none">
              {priceState.kind === "priced" ? formatPrice(priceState.total, PET_CURRENCY) : "—"}
            </div>
            {priceState.kind === "priced" && (
              <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400">
                Base + add-ons · USD
              </div>
            )}
          </div>
        </div>

        {priceState.kind === "priced" && (
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="rounded-xl bg-slate-950/50 border border-white/10 p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-2">Line items</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-200">
                  <span>Base service</span>
                  <span className="font-mono">{formatPrice(priceState.base, PET_CURRENCY)}</span>
                </div>
                {priceState.addonRows.map((a) => (
                  <div key={a.id} className="flex justify-between text-slate-300">
                    <span>+ {a.label}</span>
                    <span className="font-mono">{formatPrice(a.price, PET_CURRENCY)}</span>
                  </div>
                ))}
                {priceState.addonRows.length === 0 && (
                  <div className="text-xs text-slate-500 italic">No add-ons selected</div>
                )}
              </div>
            </div>
            <div className="rounded-xl bg-slate-950/50 border border-white/10 p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-2">Marketplace split (80/20)</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-200">
                  <span>Cleaner payout (80%)</span>
                  <span className="font-mono text-teal-300">{formatPrice(priceState.cleanerPayout, PET_CURRENCY)}</span>
                </div>
                <div className="flex justify-between text-slate-200">
                  <span>Platform fee (20%)</span>
                  <span className="font-mono text-slate-300">{formatPrice(priceState.platformFee, PET_CURRENCY)}</span>
                </div>
              </div>
              <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-500">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Same payment-protection flow as every other CLEANBAZ service.</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Book CTA */}
      <div className="mt-10 rounded-2xl border border-rose-400/30 bg-gradient-to-r from-rose-500/[0.08] via-rose-500/[0.05] to-transparent p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
        <div>
          <div className="font-display font-bold text-slate-100 text-xl">
            {priceState.kind === "priced" ? "Ready to book Pet Care?" : "Pick a service to book."}
          </div>
          <p className="text-sm text-slate-400 mt-1">
            {priceState.kind === "priced"
              ? "Continue to checkout — service address & schedule are set on the next screen."
              : "In-home visits, walks, and overnights available across the U.S."}
          </p>
        </div>
        <Button
          onClick={() => {
            const params = new URLSearchParams({
              booking_type: "pet",
              pet_service: service || "",
              pet_count: String(petCount),
            });
            if (hasDurations) params.set("pet_duration", duration);
            if (addons.length) params.set("pet_addons", addons.join(","));
            nav(`/book?${params.toString()}`);
          }}
          disabled={priceState.kind !== "priced"}
          data-testid="pet-book-cta"
          className="bg-rose-400 hover:bg-rose-300 text-slate-950 font-semibold px-6 h-11 disabled:opacity-50"
        >
          Book this pet care <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Outdoor Cleaning — full booking flow. Mirrors Pet Care UX (service → size →
// add-ons → live estimate → book) and posts to the same /jobs API with
// booking_type=outdoor. Kept in USD everywhere (matches backend calc_price).
// ---------------------------------------------------------------------------
const OUTDOOR_SERVICES = [
  { id: "yard_cleanup",     icon: Leaf,     name: "Yard Cleanup",             desc: "Rake, weed pulls, debris removal — small to xlarge yards." },
  { id: "patio_outdoor",    icon: Waves,    name: "Patio / Outdoor Cleaning", desc: "Furniture reset, sweep, wipe-down and outdoor tidy-up." },
  { id: "garage_cleaning",  icon: DoorOpen, name: "Garage Cleaning",          desc: "Sort, sweep, wipe, and organize — 1-car to xlarge bays." },
  { id: "pressure_washing", icon: Droplets, name: "Pressure Washing",         desc: "Driveway, deck, siding — soft or full-property pressure wash.", flagship: true },
];

const OUTDOOR_BASE_PRICE = {
  yard_cleanup:     { small: 65,  medium: 110, large: 165, xlarge: 250 },
  patio_outdoor:    { small: 75,  medium: 120, large: 180, xlarge: 260 },
  garage_cleaning:  { small: 95,  medium: 145, large: 195, xlarge: 265 },
  pressure_washing: { small: 95,  medium: 140, large: 220, xlarge: 350 },
};

const OUTDOOR_SIZE_LABELS = {
  yard_cleanup:     { small: "Small · under 1/8 acre", medium: "Medium · 1/8–1/4 acre", large: "Large · 1/4–1/2 acre", xlarge: "XL · 1/2 acre+" },
  patio_outdoor:    { small: "Small patio",             medium: "Medium patio",          large: "Large patio",          xlarge: "XL deck / multi-area" },
  garage_cleaning:  { small: "1-car garage",            medium: "2-car garage",          large: "3-car garage",         xlarge: "4+ car / workshop" },
  pressure_washing: { small: "Driveway only",           medium: "Deck / patio",          large: "House soft-wash",      xlarge: "Full property" },
};

const OUTDOOR_ADDONS = [
  { id: "haul_away",           label: "Debris haul-away",         price: 40 },
  { id: "weeding",             label: "Weeding",                  price: 35 },
  { id: "gutter_clearing",     label: "Gutter clearing",          price: 60 },
  { id: "furniture_reset",     label: "Furniture reset",          price: 25 },
  { id: "chemical_treatment",  label: "Chemical treatment",       price: 30 },
  { id: "extra_area",          label: "Extra area / larger yard", price: 45 },
  { id: "patio_addon",         label: "Patio cleaning",           price: 35 },
  { id: "driveway_addon",      label: "Driveway cleaning",        price: 40 },
  { id: "garage_addon",        label: "Garage cleaning",          price: 45 },
  { id: "pressure_wash_addon", label: "Pressure washing",         price: 55 },
  { id: "trash_removal",       label: "Trash / debris removal",   price: 25 },
  { id: "outdoor_furniture",   label: "Outdoor furniture clean",  price: 30 },
  { id: "walkway",             label: "Walkway cleaning",         price: 25 },
  { id: "weekend",             label: "Weekend / holiday",        price: 15 },
  { id: "extra_labor",         label: "Extra labor / time",       price: 40 },
];
const OUTDOOR_ADDON_META = Object.fromEntries(OUTDOOR_ADDONS.map((a) => [a.id, a]));

// CLEANBAZ Outdoor is priced in USD on the backend. Force USD in every display
// within the Outdoor flow, regardless of the user's global currency preference.
const OUTDOOR_CURRENCY = "USD";

function OutdoorSection() {
  const nav = useNavigate();
  const [service, setService] = useState(null);
  const [size, setSize] = useState("medium");
  const [addons, setAddons] = useState([]);

  const activeSvc = OUTDOOR_SERVICES.find((s) => s.id === service) || null;

  const toggleAddon = (id) => {
    setAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  // Live pricing — mirrors backend calc_price("outdoor") exactly.
  const priceState = useMemo(() => {
    if (!service) return { kind: "empty" };
    const base = OUTDOOR_BASE_PRICE[service]?.[size];
    if (base == null) return { kind: "empty" };
    const addonRows = addons.map((id) => {
      const meta = OUTDOOR_ADDON_META[id];
      return meta ? { id, label: meta.label, price: meta.price } : null;
    }).filter(Boolean);
    const addonTotal = addonRows.reduce((sum, r) => sum + r.price, 0);
    const total = base + addonTotal;
    const platformFee = Math.round(total * PLATFORM_FEE_PCT * 100) / 100;
    const cleanerPayout = Math.round((total - platformFee) * 100) / 100;
    return { kind: "priced", base, addonRows, addonTotal, total, platformFee, cleanerPayout };
  }, [service, size, addons]);

  return (
    <section
      id="outdoor-cleaning"
      data-testid="outdoor-cleaning-section"
      className="relative scroll-mt-28 mt-24"
    >
      <div className="absolute inset-x-0 -top-4 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />

      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 text-[10px] font-mono uppercase tracking-[0.22em]">
          <TreePine className="w-3 h-3" /> Exterior Cleaning · Outside Your Home
        </div>
        <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl leading-[1.05] text-slate-100">
          Outdoor Cleaning
          <span className="block bg-gradient-to-r from-emerald-300 to-emerald-500 bg-clip-text text-transparent">
            for the spaces around you.
          </span>
        </h2>
        <p className="mt-4 text-slate-400 text-base">
          CLEANBAZ Pros handle yards, patios, garages, and pressure-washing — the same
          payment-protected flow as every other service.
        </p>
      </div>

      {/* Service */}
      <div className="mt-12">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 1 — Choose Service</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {OUTDOOR_SERVICES.map((sv) => {
            const active = service === sv.id;
            const Icon = sv.icon;
            const min = Math.min(...Object.values(OUTDOOR_BASE_PRICE[sv.id]));
            return (
              <button
                key={sv.id}
                type="button"
                onClick={() => setService(sv.id)}
                data-testid={`outdoor-service-${sv.id}`}
                className={`relative overflow-hidden text-left rounded-2xl p-5 border transition-all ${
                  active
                    ? "border-emerald-400/70 bg-emerald-500/[0.08] shadow-[0_0_32px_rgba(16,185,129,0.22)]"
                    : "border-white/10 bg-white/[0.03] hover:border-emerald-400/30 hover:bg-white/[0.05]"
                }`}
              >
                {sv.flagship && (
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-emerald-400/20 border border-emerald-400/40 text-emerald-100 text-[10px] font-mono uppercase tracking-[0.18em]">
                      Popular
                    </Badge>
                  </div>
                )}
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${
                  active ? "bg-emerald-400/20 border-emerald-400/40 text-emerald-200" : "bg-white/[0.03] border-white/10 text-emerald-200/90"
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-4 font-display font-semibold text-slate-100 text-lg">{sv.name}</div>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{sv.desc}</p>
                <div
                  data-testid={`outdoor-service-price-${sv.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] border bg-emerald-500/10 border-emerald-400/30 text-emerald-200"
                >
                  From {formatPrice(min, OUTDOOR_CURRENCY)}
                </div>
                {active && (
                  <span className="absolute top-3 left-3 w-2 h-2 rounded-full bg-emerald-300 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Size */}
      {activeSvc && (
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 2 — Size</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {["small", "medium", "large", "xlarge"].map((sz) => {
              const active = size === sz;
              return (
                <button
                  key={sz}
                  type="button"
                  onClick={() => setSize(sz)}
                  data-testid={`outdoor-size-${sz}`}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    active
                      ? "border-emerald-400/70 bg-emerald-500/10"
                      : "border-white/10 bg-white/[0.03] hover:border-emerald-400/40"
                  }`}
                >
                  <div className="text-slate-100 font-semibold text-sm">{OUTDOOR_SIZE_LABELS[service][sz]}</div>
                  <div className="mt-1 text-xs font-mono text-emerald-200">
                    {formatPrice(OUTDOOR_BASE_PRICE[service][sz], OUTDOOR_CURRENCY)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Add-ons */}
      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 3 — Optional Add-ons</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {OUTDOOR_ADDONS.map((a) => {
            const active = addons.includes(a.id);
            return (
              <label
                key={a.id}
                data-testid={`outdoor-addon-${a.id}`}
                className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-all ${
                  active
                    ? "border-emerald-400/60 bg-emerald-500/[0.08]"
                    : "border-white/10 bg-white/[0.03] hover:border-emerald-400/30"
                }`}
              >
                <Checkbox
                  checked={active}
                  onCheckedChange={() => toggleAddon(a.id)}
                  data-testid={`outdoor-addon-toggle-${a.id}`}
                  className="border-emerald-400/50 data-[state=checked]:bg-emerald-400 data-[state=checked]:text-slate-950"
                />
                <div className="flex-1 text-sm font-semibold text-slate-100">{a.label}</div>
                <div className="font-mono text-sm text-emerald-200">+{formatPrice(a.price, OUTDOOR_CURRENCY)}</div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Live estimate */}
      <div
        data-testid="outdoor-price-estimate"
        className="mt-10 rounded-2xl border border-emerald-400/40 bg-gradient-to-br from-emerald-500/[0.10] via-slate-900/60 to-slate-900/60 p-6 sm:p-7"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-emerald-200/80">Live Estimate · USD</div>
            <div className="mt-1 font-display font-bold text-slate-100 text-2xl">
              {priceState.kind === "priced" ? "Your Outdoor Cleaning total" : "Pick a service"}
            </div>
            <div className="mt-1.5 text-sm text-slate-400 max-w-xl">
              {priceState.kind === "priced"
                ? "Payment-protected. Funds released to your CLEANBAZ Pro after completion."
                : "Choose a service above to see your live price."}
            </div>
          </div>
          <div className="text-right">
            <div data-testid="outdoor-total-price" className="font-display font-black text-4xl sm:text-5xl text-emerald-200 leading-none">
              {priceState.kind === "priced" ? formatPrice(priceState.total, OUTDOOR_CURRENCY) : "—"}
            </div>
            {priceState.kind === "priced" && (
              <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400">
                Base + add-ons · USD
              </div>
            )}
          </div>
        </div>

        {priceState.kind === "priced" && (
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="rounded-xl bg-slate-950/50 border border-white/10 p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-2">Line items</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-200">
                  <span>Base service ({OUTDOOR_SIZE_LABELS[service][size]})</span>
                  <span className="font-mono">{formatPrice(priceState.base, OUTDOOR_CURRENCY)}</span>
                </div>
                {priceState.addonRows.map((a) => (
                  <div key={a.id} className="flex justify-between text-slate-300">
                    <span>+ {a.label}</span>
                    <span className="font-mono">{formatPrice(a.price, OUTDOOR_CURRENCY)}</span>
                  </div>
                ))}
                {priceState.addonRows.length === 0 && (
                  <div className="text-xs text-slate-500 italic">No add-ons selected</div>
                )}
              </div>
            </div>
            <div className="rounded-xl bg-slate-950/50 border border-white/10 p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-2">Marketplace split (80/20)</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-200">
                  <span>Cleaner payout (80%)</span>
                  <span className="font-mono text-teal-300">{formatPrice(priceState.cleanerPayout, OUTDOOR_CURRENCY)}</span>
                </div>
                <div className="flex justify-between text-slate-200">
                  <span>Platform fee (20%)</span>
                  <span className="font-mono text-slate-300">{formatPrice(priceState.platformFee, OUTDOOR_CURRENCY)}</span>
                </div>
              </div>
              <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-500">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Same payment-protection flow as every other CLEANBAZ service.</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Book CTA */}
      <div className="mt-10 rounded-2xl border border-emerald-400/30 bg-gradient-to-r from-emerald-500/[0.08] via-emerald-500/[0.05] to-transparent p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
        <div>
          <div className="font-display font-bold text-slate-100 text-xl">
            {priceState.kind === "priced" ? "Ready to book Outdoor Cleaning?" : "Pick a service to book."}
          </div>
          <p className="text-sm text-slate-400 mt-1">
            {priceState.kind === "priced"
              ? "Continue to checkout — service address & schedule are set on the next screen."
              : "Yards, patios, garages, and pressure washing across the U.S."}
          </p>
        </div>
        <Button
          onClick={() => {
            const params = new URLSearchParams({
              booking_type: "outdoor",
              outdoor_service: service || "",
              outdoor_size: size,
            });
            if (addons.length) params.set("outdoor_addons", addons.join(","));
            nav(`/book?${params.toString()}`);
          }}
          disabled={priceState.kind !== "priced"}
          data-testid="outdoor-book-cta"
          className="bg-emerald-400 hover:bg-emerald-300 text-slate-950 font-semibold px-6 h-11 disabled:opacity-50"
        >
          Book this outdoor service <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Convenience Services — full booking flow. Mirrors Outdoor UX (service →
// size → add-ons → live estimate → book) and posts to /jobs with
// booking_type=convenience. USD everywhere (matches backend calc_price).
// ---------------------------------------------------------------------------
const CONV_SERVICES = [
  { id: "laundry",    icon: Shirt,         name: "Laundry",         desc: "Wash-and-fold pickup & drop-off. Per-load pricing." },
  { id: "organizing", icon: ClipboardList, name: "Home Organizing", desc: "Declutter, sort, and organize any room — session-based.", flagship: true },
  { id: "errands",    icon: MapPinned,     name: "Errand Services", desc: "Grocery runs, pharmacy pickups, package drop-offs, and more." },
];

const CONV_BASE_PRICE = {
  laundry:    { small: 30,  medium: 55,  large: 75,  xlarge: 95  },
  organizing: { small: 110, medium: 200, large: 280, xlarge: 350 },
  errands:    { small: 35,  medium: 65,  large: 90,  xlarge: 115 },
};

const CONV_SIZE_LABELS = {
  laundry:    { small: "1 load",      medium: "2 loads",    large: "3 loads",     xlarge: "4+ loads"   },
  organizing: { small: "2-hour session", medium: "4-hour session", large: "6-hour session", xlarge: "Full 8-hour day" },
  errands:    { small: "Up to 1 hour",   medium: "Up to 2 hours",  large: "Up to 3 hours",  xlarge: "Up to 4 hours"   },
};

const CONV_ADDONS = [
  { id: "express",             label: "Express service",         price: 20 },
  { id: "weekend",             label: "Weekend / holiday",       price: 15 },
  { id: "eco_supplies",        label: "Eco-friendly supplies",   price: 10 },
  { id: "multi_stop",          label: "Multiple stops",          price: 15 },
  { id: "extra_hour",          label: "Extra hour",              price: 30 },
  { id: "laundry_supplies",    label: "Laundry supplies",        price: 8 },
  { id: "organizing_supplies", label: "Organizing supplies",     price: 15 },
  { id: "key_pickup",          label: "Key pickup / return",     price: 10 },
];
const CONV_ADDON_META = Object.fromEntries(CONV_ADDONS.map((a) => [a.id, a]));

// Convenience is priced in USD on the backend. Force USD in every display.
const CONV_CURRENCY = "USD";

function ConvenienceSection() {
  const nav = useNavigate();
  const [service, setService] = useState(null);
  const [size, setSize] = useState("medium");
  const [addons, setAddons] = useState([]);

  const activeSvc = CONV_SERVICES.find((s) => s.id === service) || null;

  const toggleAddon = (id) => {
    setAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  // Live pricing — mirrors backend calc_price("convenience") exactly.
  const priceState = useMemo(() => {
    if (!service) return { kind: "empty" };
    const base = CONV_BASE_PRICE[service]?.[size];
    if (base == null) return { kind: "empty" };
    const addonRows = addons.map((id) => {
      const meta = CONV_ADDON_META[id];
      return meta ? { id, label: meta.label, price: meta.price } : null;
    }).filter(Boolean);
    const addonTotal = addonRows.reduce((sum, r) => sum + r.price, 0);
    const total = base + addonTotal;
    const platformFee = Math.round(total * PLATFORM_FEE_PCT * 100) / 100;
    const cleanerPayout = Math.round((total - platformFee) * 100) / 100;
    return { kind: "priced", base, addonRows, addonTotal, total, platformFee, cleanerPayout };
  }, [service, size, addons]);

  return (
    <section
      id="convenience-services"
      data-testid="convenience-services-section"
      className="relative scroll-mt-28 mt-24"
    >
      <div className="absolute inset-x-0 -top-4 h-px bg-gradient-to-r from-transparent via-violet-400/50 to-transparent" />

      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-400/30 text-violet-200 text-[10px] font-mono uppercase tracking-[0.22em]">
          <ShoppingBag className="w-3 h-3" /> Everyday Help · On Your Schedule
        </div>
        <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl leading-[1.05] text-slate-100">
          Convenience Services
          <span className="block bg-gradient-to-r from-violet-300 to-violet-500 bg-clip-text text-transparent">
            for the tasks you don't have time for.
          </span>
        </h2>
        <p className="mt-4 text-slate-400 text-base">
          CLEANBAZ Pros handle laundry, home organizing, and errands — same
          payment-protected flow as every other service.
        </p>
      </div>

      {/* Service */}
      <div className="mt-12">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 1 — Choose Service</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {CONV_SERVICES.map((sv) => {
            const active = service === sv.id;
            const Icon = sv.icon;
            const min = Math.min(...Object.values(CONV_BASE_PRICE[sv.id]));
            return (
              <button
                key={sv.id}
                type="button"
                onClick={() => setService(sv.id)}
                data-testid={`conv-service-${sv.id}`}
                className={`relative overflow-hidden text-left rounded-2xl p-5 border transition-all ${
                  active
                    ? "border-violet-400/70 bg-violet-500/[0.08] shadow-[0_0_32px_rgba(139,92,246,0.22)]"
                    : "border-white/10 bg-white/[0.03] hover:border-violet-400/30 hover:bg-white/[0.05]"
                }`}
              >
                {sv.flagship && (
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-violet-400/20 border border-violet-400/40 text-violet-100 text-[10px] font-mono uppercase tracking-[0.18em]">
                      Popular
                    </Badge>
                  </div>
                )}
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${
                  active ? "bg-violet-400/20 border-violet-400/40 text-violet-200" : "bg-white/[0.03] border-white/10 text-violet-200/90"
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-4 font-display font-semibold text-slate-100 text-lg">{sv.name}</div>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{sv.desc}</p>
                <div
                  data-testid={`conv-service-price-${sv.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] border bg-violet-500/10 border-violet-400/30 text-violet-200"
                >
                  From {formatPrice(min, CONV_CURRENCY)}
                </div>
                {active && (
                  <span className="absolute top-3 left-3 w-2 h-2 rounded-full bg-violet-300 shadow-[0_0_10px_rgba(196,181,253,0.9)]" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Size */}
      {activeSvc && (
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">
              Step 2 — {service === "laundry" ? "Load Count" : service === "organizing" ? "Session Length" : "Time Window"}
            </span>
            <span className="flex-1 h-px bg-white/10" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {["small", "medium", "large", "xlarge"].map((sz) => {
              const active = size === sz;
              return (
                <button
                  key={sz}
                  type="button"
                  onClick={() => setSize(sz)}
                  data-testid={`conv-size-${sz}`}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    active
                      ? "border-violet-400/70 bg-violet-500/10"
                      : "border-white/10 bg-white/[0.03] hover:border-violet-400/40"
                  }`}
                >
                  <div className="text-slate-100 font-semibold text-sm">{CONV_SIZE_LABELS[service][sz]}</div>
                  <div className="mt-1 text-xs font-mono text-violet-200">
                    {formatPrice(CONV_BASE_PRICE[service][sz], CONV_CURRENCY)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Add-ons */}
      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 3 — Optional Add-ons</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {CONV_ADDONS.map((a) => {
            const active = addons.includes(a.id);
            return (
              <label
                key={a.id}
                data-testid={`conv-addon-${a.id}`}
                className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-all ${
                  active
                    ? "border-violet-400/60 bg-violet-500/[0.08]"
                    : "border-white/10 bg-white/[0.03] hover:border-violet-400/30"
                }`}
              >
                <Checkbox
                  checked={active}
                  onCheckedChange={() => toggleAddon(a.id)}
                  data-testid={`conv-addon-toggle-${a.id}`}
                  className="border-violet-400/50 data-[state=checked]:bg-violet-400 data-[state=checked]:text-slate-950"
                />
                <div className="flex-1 text-sm font-semibold text-slate-100">{a.label}</div>
                <div className="font-mono text-sm text-violet-200">+{formatPrice(a.price, CONV_CURRENCY)}</div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Live estimate */}
      <div
        data-testid="conv-price-estimate"
        className="mt-10 rounded-2xl border border-violet-400/40 bg-gradient-to-br from-violet-500/[0.10] via-slate-900/60 to-slate-900/60 p-6 sm:p-7"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-violet-200/80">Live Estimate · USD</div>
            <div className="mt-1 font-display font-bold text-slate-100 text-2xl">
              {priceState.kind === "priced" ? "Your Convenience total" : "Pick a service"}
            </div>
            <div className="mt-1.5 text-sm text-slate-400 max-w-xl">
              {priceState.kind === "priced"
                ? "Payment-protected. Funds released to your CLEANBAZ Pro after completion."
                : "Choose a service above to see your live price."}
            </div>
          </div>
          <div className="text-right">
            <div data-testid="conv-total-price" className="font-display font-black text-4xl sm:text-5xl text-violet-200 leading-none">
              {priceState.kind === "priced" ? formatPrice(priceState.total, CONV_CURRENCY) : "—"}
            </div>
            {priceState.kind === "priced" && (
              <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400">
                Base + add-ons · USD
              </div>
            )}
          </div>
        </div>

        {priceState.kind === "priced" && (
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="rounded-xl bg-slate-950/50 border border-white/10 p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-2">Line items</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-200">
                  <span>Base service ({CONV_SIZE_LABELS[service][size]})</span>
                  <span className="font-mono">{formatPrice(priceState.base, CONV_CURRENCY)}</span>
                </div>
                {priceState.addonRows.map((a) => (
                  <div key={a.id} className="flex justify-between text-slate-300">
                    <span>+ {a.label}</span>
                    <span className="font-mono">{formatPrice(a.price, CONV_CURRENCY)}</span>
                  </div>
                ))}
                {priceState.addonRows.length === 0 && (
                  <div className="text-xs text-slate-500 italic">No add-ons selected</div>
                )}
              </div>
            </div>
            <div className="rounded-xl bg-slate-950/50 border border-white/10 p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-2">Marketplace split (80/20)</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-200">
                  <span>Cleaner payout (80%)</span>
                  <span className="font-mono text-teal-300">{formatPrice(priceState.cleanerPayout, CONV_CURRENCY)}</span>
                </div>
                <div className="flex justify-between text-slate-200">
                  <span>Platform fee (20%)</span>
                  <span className="font-mono text-slate-300">{formatPrice(priceState.platformFee, CONV_CURRENCY)}</span>
                </div>
              </div>
              <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-500">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Same payment-protection flow as every other CLEANBAZ service.</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Book CTA */}
      <div className="mt-10 rounded-2xl border border-violet-400/30 bg-gradient-to-r from-violet-500/[0.08] via-violet-500/[0.05] to-transparent p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
        <div>
          <div className="font-display font-bold text-slate-100 text-xl">
            {priceState.kind === "priced" ? "Ready to book Convenience?" : "Pick a service to book."}
          </div>
          <p className="text-sm text-slate-400 mt-1">
            {priceState.kind === "priced"
              ? "Continue to checkout — pickup / service address & schedule are set on the next screen."
              : "Laundry, organizing, and errands available across the U.S."}
          </p>
        </div>
        <Button
          onClick={() => {
            const params = new URLSearchParams({
              booking_type: "convenience",
              convenience_service: service || "",
              convenience_size: size,
            });
            if (addons.length) params.set("convenience_addons", addons.join(","));
            nav(`/book?${params.toString()}`);
          }}
          disabled={priceState.kind !== "priced"}
          data-testid="conv-book-cta"
          className="bg-violet-400 hover:bg-violet-300 text-slate-950 font-semibold px-6 h-11 disabled:opacity-50"
        >
          Book this service <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Home Cleaning deep-dive. Mirrors Outdoor/Pet UX and posts to /jobs with
// booking_type=home. Uses central ADDON_CATALOG (mirrored below) — change
// prices in Book.jsx / server.py ADDON_CATALOG to reprice globally.
// ---------------------------------------------------------------------------
const HOME_SERVICE_TYPES = [
  { id: "standard", name: "Standard Cleaning",        desc: "Weekly / bi-weekly upkeep — dusting, vacuuming, kitchen & baths.", deep: false },
  { id: "deep",     name: "Deep Cleaning",            desc: "Top-to-bottom reset — 1.5× multiplier applied automatically.",   deep: true },
  { id: "move_in",  name: "Move-In Cleaning",         desc: "Full move-in prep — deep clean with inside-of-cabinet detail.",    deep: true },
  { id: "move_out", name: "Move-Out Cleaning",        desc: "Deposit-ready — deep clean with appliance interiors.",            deep: true, flagship: true },
  { id: "airbnb",   name: "Airbnb / Vacation Rental", desc: "Turnover cleaning — laundry-ready, guest-fresh finish.",          deep: true },
];
const HOME_ADDONS_LIST = [
  { id: "fridge",             label: "Inside fridge",            price: 45 },
  { id: "oven",               label: "Inside oven",              price: 45 },
  { id: "interior_windows",   label: "Interior windows",         price: 35 },
  { id: "laundry",            label: "Laundry (wash & fold)",    price: 25 },
  { id: "cabinets",           label: "Inside cabinets",          price: 40 },
  { id: "baseboards",         label: "Baseboards",               price: 30 },
  { id: "inside_closets",     label: "Inside closets",           price: 35 },
  { id: "pet_hair",           label: "Pet-hair treatment",       price: 25 },
  { id: "extra_bedroom",      label: "Extra bedroom",            price: 40 },
  { id: "extra_bathroom",     label: "Extra bathroom",           price: 20 },
  { id: "deep_clean_upgrade", label: "Deep-clean upgrade",       price: 60 },
];
const HOME_ADDON_META = Object.fromEntries(HOME_ADDONS_LIST.map((a) => [a.id, a]));
const HOME_CURRENCY = "USD";

function HomeCleaningSection() {
  const nav = useNavigate();
  const [service, setService] = useState("standard");
  const [beds, setBeds] = useState(2);
  const [baths, setBaths] = useState(1);
  const [addons, setAddons] = useState([]);

  const activeSvc = HOME_SERVICE_TYPES.find((s) => s.id === service);
  const toggleAddon = (id) => setAddons((prev) => prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]);
  const bump = (setter, val, min, max, delta) => setter(Math.max(min, Math.min(max, val + delta)));

  // Live pricing — mirrors backend calc_price("home").
  const priceState = useMemo(() => {
    const base = 65 + 40 * beds + 20 * baths;
    const subtotal = activeSvc.deep ? Math.round(base * 1.5 * 100) / 100 : base;
    const addonRows = addons.map((id) => {
      const meta = HOME_ADDON_META[id];
      return meta ? { id, label: meta.label, price: meta.price } : null;
    }).filter(Boolean);
    const addonTotal = addonRows.reduce((s, r) => s + r.price, 0);
    const total = subtotal + addonTotal;
    const platformFee = Math.round(total * PLATFORM_FEE_PCT * 100) / 100;
    const cleanerPayout = Math.round((total - platformFee) * 100) / 100;
    return { base, subtotal, addonRows, addonTotal, total, platformFee, cleanerPayout };
  }, [service, beds, baths, addons, activeSvc]);

  return (
    <section id="home-cleaning" data-testid="home-cleaning-section" className="relative scroll-mt-28 mt-24">
      <div className="absolute inset-x-0 -top-4 h-px bg-gradient-to-r from-transparent via-teal-400/50 to-transparent" />
      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-400/30 text-teal-200 text-[10px] font-mono uppercase tracking-[0.22em]">
          <Home className="w-3 h-3" /> Home Cleaning · Residential
        </div>
        <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl leading-[1.05] text-slate-100">
          Home Cleaning
          <span className="block bg-gradient-to-r from-teal-300 to-teal-500 bg-clip-text text-transparent">on your schedule.</span>
        </h2>
        <p className="mt-4 text-slate-400 text-base">Standard weekly upkeep to full move-out deep cleans — same payment-protected flow.</p>
      </div>

      <div className="mt-12">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 1 — Service Type</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {HOME_SERVICE_TYPES.map((sv) => {
            const active = service === sv.id;
            return (
              <button key={sv.id} type="button" onClick={() => setService(sv.id)} data-testid={`home-service-${sv.id}`}
                className={`relative overflow-hidden text-left rounded-2xl p-5 border transition-all ${
                  active ? "border-teal-400/70 bg-teal-500/[0.08] shadow-[0_0_32px_rgba(20,184,166,0.22)]"
                         : "border-white/10 bg-white/[0.03] hover:border-teal-400/30"}`}>
                {sv.flagship && (
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-teal-400/20 border border-teal-400/40 text-teal-100 text-[10px] font-mono uppercase tracking-[0.18em]">Popular</Badge>
                  </div>
                )}
                <div className="font-display font-semibold text-slate-100 text-lg">{sv.name}</div>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{sv.desc}</p>
                {sv.deep && (
                  <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] border bg-teal-500/10 border-teal-400/30 text-teal-200">
                    Deep-clean 1.5× multiplier
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 2 — Property Size</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
          {[
            { label: "Bedrooms", value: beds, set: setBeds, tid: "home-beds", min: 0, max: 10 },
            { label: "Bathrooms", value: baths, set: setBaths, tid: "home-baths", min: 0, max: 8 },
          ].map((row) => (
            <div key={row.tid} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex-1">
                <div className="text-slate-100 font-semibold text-sm">{row.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{row.label === "Bedrooms" ? "$40 per bedroom" : "$20 per bathroom"}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => bump(row.set, row.value, row.min, row.max, -1)}
                  disabled={row.value <= row.min} data-testid={`${row.tid}-dec`}
                  className="w-9 h-9 p-0 rounded-full border-teal-400/40 text-teal-200 hover:bg-teal-500/10"><Minus className="w-4 h-4" /></Button>
                <div data-testid={`${row.tid}-value`} className="w-8 text-center font-display font-bold text-xl text-slate-100">{row.value}</div>
                <Button size="sm" variant="outline" onClick={() => bump(row.set, row.value, row.min, row.max, 1)}
                  disabled={row.value >= row.max} data-testid={`${row.tid}-inc`}
                  className="w-9 h-9 p-0 rounded-full border-teal-400/40 text-teal-200 hover:bg-teal-500/10"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 3 — Add-ons</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {HOME_ADDONS_LIST.map((a) => {
            const active = addons.includes(a.id);
            return (
              <label key={a.id} data-testid={`home-addon-${a.id}`}
                className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-all ${
                  active ? "border-teal-400/60 bg-teal-500/[0.08]" : "border-white/10 bg-white/[0.03] hover:border-teal-400/30"}`}>
                <Checkbox checked={active} onCheckedChange={() => toggleAddon(a.id)} data-testid={`home-addon-toggle-${a.id}`}
                  className="border-teal-400/50 data-[state=checked]:bg-teal-400 data-[state=checked]:text-slate-950" />
                <div className="flex-1 text-sm font-semibold text-slate-100">{a.label}</div>
                <div className="font-mono text-sm text-teal-200">+{formatPrice(a.price, HOME_CURRENCY)}</div>
              </label>
            );
          })}
        </div>
      </div>

      <div data-testid="home-price-estimate" className="mt-10 rounded-2xl border border-teal-400/40 bg-gradient-to-br from-teal-500/[0.10] via-slate-900/60 to-slate-900/60 p-6 sm:p-7">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-teal-200/80">Live Estimate · USD</div>
            <div className="mt-1 font-display font-bold text-slate-100 text-2xl">Your Home Cleaning total</div>
            <div className="mt-1.5 text-sm text-slate-400 max-w-xl">Payment-protected. Funds released after completion.</div>
          </div>
          <div className="text-right">
            <div data-testid="home-total-price" className="font-display font-black text-4xl sm:text-5xl text-teal-200 leading-none">
              {formatPrice(priceState.total, HOME_CURRENCY)}
            </div>
            <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400">Base + add-ons · USD</div>
          </div>
        </div>
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div className="rounded-xl bg-slate-950/50 border border-white/10 p-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-2">Line items</div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-200">
                <span>Base · {beds}bd / {baths}ba {activeSvc.deep ? "· Deep 1.5×" : ""}</span>
                <span className="font-mono">{formatPrice(priceState.subtotal, HOME_CURRENCY)}</span>
              </div>
              {priceState.addonRows.map((a) => (
                <div key={a.id} className="flex justify-between text-slate-300">
                  <span>+ {a.label}</span>
                  <span className="font-mono">{formatPrice(a.price, HOME_CURRENCY)}</span>
                </div>
              ))}
              {priceState.addonRows.length === 0 && <div className="text-xs text-slate-500 italic">No add-ons selected</div>}
            </div>
          </div>
          <div className="rounded-xl bg-slate-950/50 border border-white/10 p-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-2">Marketplace split (80/20)</div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-200"><span>Cleaner payout (80%)</span><span className="font-mono text-teal-300">{formatPrice(priceState.cleanerPayout, HOME_CURRENCY)}</span></div>
              <div className="flex justify-between text-slate-200"><span>Platform fee (20%)</span><span className="font-mono text-slate-300">{formatPrice(priceState.platformFee, HOME_CURRENCY)}</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 rounded-2xl border border-teal-400/30 bg-gradient-to-r from-teal-500/[0.08] via-teal-500/[0.05] to-transparent p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
        <div>
          <div className="font-display font-bold text-slate-100 text-xl">Ready to book?</div>
          <p className="text-sm text-slate-400 mt-1">Continue to checkout — service address & schedule on the next screen.</p>
        </div>
        <Button
          onClick={() => {
            const params = new URLSearchParams({
              booking_type: "home",
              beds: String(beds),
              baths: String(baths),
              deep: activeSvc.deep ? "1" : "0",
              home_service: service,
            });
            if (addons.length) params.set("addons", addons.join(","));
            nav(`/book?${params.toString()}`);
          }}
          data-testid="home-book-cta"
          className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold px-6 h-11"
        >
          Book this cleaning <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Commercial Cleaning deep-dive. Posts to /jobs with booking_type=business.
// ---------------------------------------------------------------------------
const COMMERCIAL_SPACE_TYPES = [
  { id: "office", name: "Office",  desc: "Cubicles, meeting rooms, breakrooms — the CLEANBAZ default.",   mult: 1.0 },
  { id: "retail", name: "Retail",  desc: "Showrooms, boutiques, storefronts — 10% multiplier.",           mult: 1.1 },
  { id: "gym",    name: "Gym",     desc: "Fitness spaces — locker-room + high-sweat protocol, 30% mult.", mult: 1.3 },
  { id: "clinic", name: "Clinic",  desc: "Medical / dental / clinical — sanitizing protocol, 40% mult.",  mult: 1.4, flagship: true },
];
const COMMERCIAL_SQFT_BRACKETS = [
  { id: "under_1500", label: "Under 1,500 sq ft", price: 175 },
  { id: "1500_3500",  label: "1,500 – 3,500 sq ft", price: 275 },
  { id: "3500_5000",  label: "3,500 – 5,000 sq ft", price: 375 },
  { id: "5000_plus",  label: "5,000+ sq ft",       price: 500 },
];
const COMMERCIAL_ADDONS_LIST = [
  { id: "interior_windows",  label: "Interior windows",         price: 55 },
  { id: "floor_care",        label: "Floor care",               price: 75 },
  { id: "carpet_cleaning",   label: "Carpet cleaning",          price: 85 },
  { id: "restroom_deep",     label: "Restroom deep clean",      price: 55 },
  { id: "kitchen_breakroom", label: "Kitchen / breakroom",      price: 65 },
  { id: "trash_removal",     label: "Trash removal",            price: 25 },
  { id: "disinfecting",      label: "Disinfecting / sanitizing", price: 60 },
  { id: "high_touch",        label: "High-touch-area cleaning", price: 40 },
  { id: "after_hours",       label: "After-hours service",      price: 75 },
  { id: "post_construction", label: "Post-construction cleanup", price: 150 },
];
const COMMERCIAL_ADDON_META = Object.fromEntries(COMMERCIAL_ADDONS_LIST.map((a) => [a.id, a]));
const COMMERCIAL_CURRENCY = "USD";

function CommercialCleaningSection() {
  const nav = useNavigate();
  const [spaceType, setSpaceType] = useState("office");
  const [sqftBracket, setSqftBracket] = useState("1500_3500");
  const [deep, setDeep] = useState(false);
  const [addons, setAddons] = useState([]);

  const activeSpace = COMMERCIAL_SPACE_TYPES.find((s) => s.id === spaceType);
  const activeSqft = COMMERCIAL_SQFT_BRACKETS.find((s) => s.id === sqftBracket);
  const toggleAddon = (id) => setAddons((prev) => prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]);

  const priceState = useMemo(() => {
    let subtotal = activeSqft.price * activeSpace.mult;
    if (deep) subtotal = subtotal * 1.5;
    subtotal = Math.round(subtotal * 100) / 100;
    const addonRows = addons.map((id) => {
      const meta = COMMERCIAL_ADDON_META[id];
      return meta ? { id, label: meta.label, price: meta.price } : null;
    }).filter(Boolean);
    const addonTotal = addonRows.reduce((s, r) => s + r.price, 0);
    const total = Math.round((subtotal + addonTotal) * 100) / 100;
    const platformFee = Math.round(total * PLATFORM_FEE_PCT * 100) / 100;
    const cleanerPayout = Math.round((total - platformFee) * 100) / 100;
    return { subtotal, addonRows, addonTotal, total, platformFee, cleanerPayout };
  }, [spaceType, sqftBracket, deep, addons, activeSpace, activeSqft]);

  return (
    <section id="commercial-cleaning" data-testid="commercial-cleaning-section" className="relative scroll-mt-28 mt-24">
      <div className="absolute inset-x-0 -top-4 h-px bg-gradient-to-r from-transparent via-sky-400/50 to-transparent" />
      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-400/30 text-sky-200 text-[10px] font-mono uppercase tracking-[0.22em]">
          <Building2 className="w-3 h-3" /> Commercial Cleaning · Business
        </div>
        <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl leading-[1.05] text-slate-100">
          Commercial Cleaning
          <span className="block bg-gradient-to-r from-sky-300 to-sky-500 bg-clip-text text-transparent">for offices, retail, gyms & clinics.</span>
        </h2>
        <p className="mt-4 text-slate-400 text-base">Sqft-tiered pricing with space-type multipliers and enterprise add-ons.</p>
      </div>

      <div className="mt-12">
        <div className="flex items-center gap-3 mb-4"><span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 1 — Space Type</span><span className="flex-1 h-px bg-white/10" /></div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {COMMERCIAL_SPACE_TYPES.map((sv) => {
            const active = spaceType === sv.id;
            return (
              <button key={sv.id} type="button" onClick={() => setSpaceType(sv.id)} data-testid={`commercial-space-${sv.id}`}
                className={`relative overflow-hidden text-left rounded-2xl p-5 border transition-all ${
                  active ? "border-sky-400/70 bg-sky-500/[0.08] shadow-[0_0_32px_rgba(56,189,248,0.22)]"
                         : "border-white/10 bg-white/[0.03] hover:border-sky-400/30"}`}>
                {sv.flagship && (
                  <div className="absolute top-3 right-3"><Badge className="bg-sky-400/20 border border-sky-400/40 text-sky-100 text-[10px] font-mono uppercase tracking-[0.18em]">Popular</Badge></div>
                )}
                <div className="font-display font-semibold text-slate-100 text-lg">{sv.name}</div>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{sv.desc}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.18em] border bg-sky-500/10 border-sky-400/30 text-sky-200">
                  {sv.mult.toFixed(1)}× multiplier
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4"><span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 2 — Square Footage</span><span className="flex-1 h-px bg-white/10" /></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {COMMERCIAL_SQFT_BRACKETS.map((sq) => {
            const active = sqftBracket === sq.id;
            return (
              <button key={sq.id} type="button" onClick={() => setSqftBracket(sq.id)} data-testid={`commercial-sqft-${sq.id}`}
                className={`rounded-xl border p-4 text-left transition-all ${active ? "border-sky-400/70 bg-sky-500/10" : "border-white/10 bg-white/[0.03] hover:border-sky-400/40"}`}>
                <div className="text-slate-100 font-semibold text-sm">{sq.label}</div>
                <div className="mt-1 text-xs font-mono text-sky-200">Base {formatPrice(sq.price, COMMERCIAL_CURRENCY)}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-8 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 max-w-md">
        <Checkbox checked={deep} onCheckedChange={() => setDeep((v) => !v)} data-testid="commercial-deep-toggle"
          className="border-sky-400/50 data-[state=checked]:bg-sky-400 data-[state=checked]:text-slate-950" />
        <div className="flex-1">
          <div className="text-slate-100 font-semibold text-sm">Deep-clean upgrade</div>
          <div className="text-xs text-slate-500 mt-0.5">Applies a 1.5× multiplier to the base rate.</div>
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4"><span className="text-xs font-mono uppercase tracking-[0.24em] text-slate-400">Step 3 — Add-ons</span><span className="flex-1 h-px bg-white/10" /></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {COMMERCIAL_ADDONS_LIST.map((a) => {
            const active = addons.includes(a.id);
            return (
              <label key={a.id} data-testid={`commercial-addon-${a.id}`}
                className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-all ${
                  active ? "border-sky-400/60 bg-sky-500/[0.08]" : "border-white/10 bg-white/[0.03] hover:border-sky-400/30"}`}>
                <Checkbox checked={active} onCheckedChange={() => toggleAddon(a.id)} data-testid={`commercial-addon-toggle-${a.id}`}
                  className="border-sky-400/50 data-[state=checked]:bg-sky-400 data-[state=checked]:text-slate-950" />
                <div className="flex-1 text-sm font-semibold text-slate-100">{a.label}</div>
                <div className="font-mono text-sm text-sky-200">+{formatPrice(a.price, COMMERCIAL_CURRENCY)}</div>
              </label>
            );
          })}
        </div>
      </div>

      <div data-testid="commercial-price-estimate" className="mt-10 rounded-2xl border border-sky-400/40 bg-gradient-to-br from-sky-500/[0.10] via-slate-900/60 to-slate-900/60 p-6 sm:p-7">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-sky-200/80">Live Estimate · USD</div>
            <div className="mt-1 font-display font-bold text-slate-100 text-2xl">Your Commercial Cleaning total</div>
            <div className="mt-1.5 text-sm text-slate-400 max-w-xl">Payment-protected. Funds released after completion.</div>
          </div>
          <div className="text-right">
            <div data-testid="commercial-total-price" className="font-display font-black text-4xl sm:text-5xl text-sky-200 leading-none">{formatPrice(priceState.total, COMMERCIAL_CURRENCY)}</div>
            <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400">Base + add-ons · USD</div>
          </div>
        </div>
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div className="rounded-xl bg-slate-950/50 border border-white/10 p-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-2">Line items</div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-200"><span>Base · {activeSpace.name} · {activeSqft.label}{deep ? " · Deep 1.5×" : ""}</span><span className="font-mono">{formatPrice(priceState.subtotal, COMMERCIAL_CURRENCY)}</span></div>
              {priceState.addonRows.map((a) => (<div key={a.id} className="flex justify-between text-slate-300"><span>+ {a.label}</span><span className="font-mono">{formatPrice(a.price, COMMERCIAL_CURRENCY)}</span></div>))}
              {priceState.addonRows.length === 0 && <div className="text-xs text-slate-500 italic">No add-ons selected</div>}
            </div>
          </div>
          <div className="rounded-xl bg-slate-950/50 border border-white/10 p-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-2">Marketplace split (80/20)</div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-200"><span>Cleaner payout (80%)</span><span className="font-mono text-teal-300">{formatPrice(priceState.cleanerPayout, COMMERCIAL_CURRENCY)}</span></div>
              <div className="flex justify-between text-slate-200"><span>Platform fee (20%)</span><span className="font-mono text-slate-300">{formatPrice(priceState.platformFee, COMMERCIAL_CURRENCY)}</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 rounded-2xl border border-sky-400/30 bg-gradient-to-r from-sky-500/[0.08] via-sky-500/[0.05] to-transparent p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
        <div>
          <div className="font-display font-bold text-slate-100 text-xl">Ready to book Commercial?</div>
          <p className="text-sm text-slate-400 mt-1">Continue to checkout — service address & schedule on the next screen.</p>
        </div>
        <Button
          onClick={() => {
            const params = new URLSearchParams({ booking_type: "business", space: spaceType, sqft: sqftBracket, deep: deep ? "1" : "0" });
            if (addons.length) params.set("addons", addons.join(","));
            nav(`/book?${params.toString()}`);
          }}
          data-testid="commercial-book-cta"
          className="bg-sky-400 hover:bg-sky-300 text-slate-950 font-semibold px-6 h-11"
        >
          Book Commercial <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </section>
  );
}

export default function Services() {
  const scrollToCar = () => {
    const el = document.getElementById("car-cleaning");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToPet = () => {
    const el = document.getElementById("pet-care");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToOutdoor = () => {
    const el = document.getElementById("outdoor-cleaning");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToConvenience = () => {
    const el = document.getElementById("convenience-services");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToHome = () => {
    const el = document.getElementById("home-cleaning");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToCommercial = () => {
    const el = document.getElementById("commercial-cleaning");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-slate-950 text-slate-100" data-testid="services-page">
      {/* Header */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none opacity-40" style={{
          background: "radial-gradient(1200px 400px at 50% -10%, rgba(20,184,166,0.18), transparent 60%)"
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14 relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-400/30 text-teal-200 text-[10px] font-mono uppercase tracking-[0.22em]">
              <Sparkles className="w-3 h-3" /> Service Catalog
            </div>
            <h1 className="mt-4 font-display font-black text-5xl sm:text-6xl leading-[1.02]">
              Everything CLEANBAZ
              <span className="block bg-gradient-to-r from-teal-300 to-teal-500 bg-clip-text text-transparent">cleans for you.</span>
            </h1>
            <p className="mt-5 text-slate-400 text-base sm:text-lg">
              Six categories. One vetted marketplace. All lines are live and booking today,
              backed by the same payment-protected flow and CLEANBAZ Pro standards.
            </p>
          </div>
        </div>
      </section>

      {/* Category grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {CATEGORIES.map((c) => (
            <CategoryCard key={c.id} cat={c}
              onCarClick={scrollToCar}
              onPetClick={scrollToPet}
              onOutdoorClick={scrollToOutdoor}
              onConvenienceClick={scrollToConvenience}
              onHomeClick={scrollToHome}
              onCommercialClick={scrollToCommercial}
            />
          ))}
        </div>

        {/* Home Cleaning deep-dive */}
        <HomeCleaningSection />

        {/* Commercial Cleaning deep-dive */}
        <CommercialCleaningSection />

        {/* Car Cleaning deep-dive */}
        <CarCleaningSection />

        {/* Pet Care deep-dive */}
        <PetCareSection />

        {/* Outdoor Cleaning deep-dive */}
        <OutdoorSection />

        {/* Convenience Services deep-dive */}
        <ConvenienceSection />
      </section>
    </div>
  );
}
